import { useState, useMemo } from 'react';
import styles from './CreditLinePaymentDatePage.module.scss';
import { Button, Calendar } from '@/ui/components';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/routes/routes';
import { calendar } from '@/assets/images/emojis';
import { useApplication } from '../../hooks';
import { defaultBillDate } from '@/lib/loans.utility';

/**
 * Get disabled dates for the next 3 months
 * Disables days 29, 30, 31 in each of the next 3 months (if they exist)
 * This matches the React Native calendar implementation
 */
const getDisabledDatesForNext3Months = (): Date[] => {
  const disabledDates: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Loop through the next 3 months
  for (let i = 0; i < 3; i++) {
    const date = new Date(today);
    date.setMonth(today.getMonth() + i);
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-indexed

    // Check days 29, 30, 31 - only add if they exist in that month
    [29, 30, 31].forEach((day) => {
      const testDate = new Date(year, month, day);
      // If the date is valid and still in the same month, it exists
      if (
        testDate.getFullYear() === year &&
        testDate.getMonth() === month &&
        testDate.getDate() === day
      ) {
        // Normalize to midnight to avoid timezone issues
        testDate.setHours(0, 0, 0, 0);
        disabledDates.push(testDate);
      }
    });
  }

  return disabledDates;
};

export function CreditLinePaymentDatePage() {
  const navigate = useNavigate();
  const { creditLineDraft, setCreditLinePayDate } = useApplication();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    creditLineDraft?.firstBillDate ?? defaultBillDate()
  );

  // Calculate year range: this year, one back, and one forward
  const { startMonth, endMonth } = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();

    // Start month: January 1st of one year back
    const start = new Date(currentYear - 1, 0, 1);

    // End month: December 1st of one year forward (DayPicker uses first day of month)
    const end = new Date(currentYear + 1, 11, 1);

    return { startMonth: start, endMonth: end };
  }, []);

  // Calculate minDate (tomorrow) and maxDate (45 days from now)
  const { minDate, maxDate, disabledDates } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Min date: tomorrow (24 hours from now)
    const min = new Date(today);
    min.setDate(today.getDate() + 1);

    // Max date: 45 days from now
    const max = new Date(today);
    max.setDate(today.getDate() + 45);

    // Get disabled dates for next 3 months
    const disabled = getDisabledDatesForNext3Months();

    return { minDate: min, maxDate: max, disabledDates: disabled };
  }, []);

  const handleSave = () => {
    if (selectedDate) {
      setCreditLinePayDate(selectedDate);
      navigate(routes.application.creditLineConfirmation);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.emojiContainer}>
            <img src={calendar} alt="Calendar" className={styles.emoji} />
          </div>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>Alege data scadenței</h1>
            <p className={styles.description}>
              Setează o zi comodă pentru tine — ideal, după ce îți intră
              veniturile.
            </p>
          </div>
        </div>

        <div className={styles.calendarContainer}>
          <Calendar
            selected={selectedDate}
            onSelect={handleDateSelect}
            defaultMonth={selectedDate}
            startMonth={startMonth}
            endMonth={endMonth}
            captionLayout="label"
            fromDate={minDate}
            toDate={maxDate}
            disabled={disabledDates.length > 0 ? disabledDates : undefined}
          />
        </div>
      </div>
      <div className={styles.bottomSection}>
        <Button
          trackingId="select_payment_date"
          onClick={handleSave}
          className={styles.saveButton}
          text="Salvează"
        />
      </div>
    </div>
  );
}
